import { FCC } from "@/types/common";
import { TCompanyForm } from "@/features/company/types/CompanyForm";
import { CompanyBasicInfo } from "@/features/company/components/company-info/CompanyBasicInfo";
import { useForm, useWatch } from "react-hook-form";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { Stack, styled, Typography } from "@mui/material";
import { Form } from "@/components/elements/Form";
import { useDialog } from "@/hooks/useDialog";
import { useGetCompanySchema } from "@/features/company/hooks/useGetCompanySchema";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { createCompanyApi, getCompanyExists, updateCompanyApi, useCompanyInfo } from "@/api/services/main/company";
import { showError } from "@/utils/error";
import { CompanyTitle } from "@/features/company/components/CompanyTitle";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { CompanyContactInfo } from "@/features/company/components/company-info/CompanyContactInfo";
import { CompanyAdditionalInfo } from "@/features/company/components/company-info/CompanyAdditionalInfo";
import { CompanyContractInfo } from "@/features/company/components/company-detail/CompanyContractInfo";
import { CompanyHandledIndustryForm } from "@/features/company/components/company-info/CompanyHandledIndustryForm";
import { Button } from "@/components/elements/Button";
import { CompanyFooterContainer } from "@/features/company/components/CompanyFooterContainer";
import { IconSave } from "@/assets/icons";
import { DuplicateCompanyModal } from "@/features/company/components/DuplicateCompanyModal";
import { TGetCompanyExistsDto } from "@/api/services/main/company/dtos/get-company.dto";
import { convertCompanyFormDataReq } from "@/features/company/utiles/convertCompanyFormData";
import { TCreateCompanyResDto } from "@/api/services/main/company/dtos/create-company-req.dto";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { queryClient } from "@/constants/queryClient";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { toast } from "@/utils/toast";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";
import { truncateWithEllipsis } from "@/utils/string";

type TCompanyFormProps = {
  defaultValues?: Partial<TCompanyForm>;
  readonly?: boolean;
};
export const CompanyForm: FCC<TCompanyFormProps> = ({ defaultValues, readonly }) => {
  const navigate = useNavigate();
  const { userType } = useInitSetup();
  const isCompanyUser = userType === USER_TYPE.COMPANY_USER;
  const { companyId } = useParams();
  const { data: companyData } = useCompanyInfo();
  const confirmSave = useDialog();
  const { companySchema } = useGetCompanySchema({ isCompanyUser });
  const methods = useForm<TCompanyForm>({
    defaultValues: {
      ...defaultValues,
    },
    resolver: yupResolver(companySchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { getValues } = methods;
  const [confirmMessage] = useState<string>();
  const [isDisableButton, setIsDisableButton] = useState(false);
  const [companyExist, setCompanyExist] = useState<TGetCompanyExistsDto[]>([]);
  const [removedLockedIndustryNames, setRemovedLockedIndustryNames] = useState<string[]>([]);
  const companyName = useWatch({ control: methods.control, name: "companyName" });
  const managementNumber = useWatch({ control: methods.control, name: "managementNumber" });
  const duplicateModal = useDialog();
  const industryConfirmDialog = useDialog();

  // Original 特定産業分野 objects (loaded from API) carry `hasLearner` flag for non-archived learners.
  // If a locked industry is no longer present in any of the 3 slots after edit, warn before submit.
  const getRemovedLockedIndustryNames = (): string[] => {
    const values = getValues();
    const currentIds = [
      values.companySpecificIndustryId1,
      values.companySpecificIndustryId2,
      values.companySpecificIndustryId3,
    ]
      .filter((id) => id != null && (id as unknown) !== "")
      .map((id) => Number(id));
    const originals = [
      values.companySpecificIndustry1,
      values.companySpecificIndustry2,
      values.companySpecificIndustry3,
    ];
    return originals
      .filter((ind) => ind?.hasLearner && ind?.id != null && !currentIds.includes(Number(ind.id)))
      .map((ind) => ind!.companySpecificIndustry);
  };

  const onSubmit = async () => {
    const removedNames = getRemovedLockedIndustryNames();
    if (removedNames.length > 0) {
      setRemovedLockedIndustryNames(removedNames);
      const confirmed = await industryConfirmDialog.show();
      if (!confirmed) return;
    }
    await handleGetCompanyExist();
  };

  const handleConfirmIndustryChange = () => {
    industryConfirmDialog.confirm();
  };

  const handleCancelIndustryChange = () => {
    industryConfirmDialog.cancel();
  };

  const handleDuplicateClose = () => duplicateModal.cancel();

  const handleGetCompanyExist = async () => {
    try {
      const { data: companyExists } = await getCompanyExists(companyName, companyId);
      if (companyExists && companyExists.length > 0) {
        setCompanyExist(companyExists);
        duplicateModal.show();
      } else {
        await handleSaveData();
      }
    } catch (e) {
      showError(e);
    }
  };

  const handleSaveData = async () => {
    setIsDisableButton(true);
    try {
      const payload = convertCompanyFormDataReq(getValues(), isCompanyUser);
      let rs: TCreateCompanyResDto;
      let route: string;
      if (companyId) {
        rs = await updateCompanyApi(companyId, payload);
        route = getDynamicRoute(AppRoutes.companyDetail, { companyId: companyId });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL, companyId] });
      } else {
        rs = await createCompanyApi(payload);
        route = AppRoutes.companyList;
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST] });
      }
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setIsDisableButton(false);
      navigate(route);
    } catch (e) {
      setIsDisableButton(false);
      showError(e);
    }
  };

  const displayName = companyName || "(会社名未入力)";

  return (
    <>
      <CompanyTitle title={defaultValues?.companyName || "新規所属機関登録"} managementNumber={managementNumber} />

      <Stack mt={3}>
        <Form methods={methods} onSubmit={onSubmit} schema={companySchema} initialValues={defaultValues}>
          <MenuNavigate
            menuPrefix={
              <Typography variant="body14" sx={{ color: "#0B0C0D", px: 2, py: 1, fontWeight: 700 }}>
                {truncateWithEllipsis(displayName, 15)}
              </Typography>
            }
            contents={[
              { title: "基本情報", content: <CompanyBasicInfo readonly={readonly} /> },
              ...(!isCompanyUser ? [{ title: "担当者情報", content: <CompanyContactInfo /> }] : []),
              { title: "その他の情報", content: <CompanyAdditionalInfo /> },
              ...(isCompanyUser ? [{ title: "取り扱う分野", content: <CompanyHandledIndustryForm /> }] : []),
              ...(isCompanyUser
                ? [{
                    title: "契約情報",
                    content: (
                      <CompanyContractInfo data={companyData?.data ?? {}} />
                    ),
                  }]
                : []),
            ]}
            contentWidth="900px"
            contentAffix={
              <CompanyFooterContainer>
                <Stack direction="row" gap={3}>
                  <ButtonSave
                    disabled={isDisableButton}
                    type="submit"
                    variant="primary"
                    size="sm"
                    startIcon={<IconSave />}
                  >
                    保存
                  </ButtonSave>
                </Stack>
              </CompanyFooterContainer>
            }
          />
        </Form>
      </Stack>

      <ConfirmSaveModal open={confirmSave.open} onOk={confirmSave.confirm} onClose={confirmSave.cancel}>
        <Typography component="p" variant="subtitle1">
          {confirmMessage}
        </Typography>
      </ConfirmSaveModal>
      <ConfirmSaveModal
        open={industryConfirmDialog.open}
        onOk={handleConfirmIndustryChange}
        onClose={handleCancelIndustryChange}
      >
        <Typography component="p" variant="subtitle1">
          {`すでに${removedLockedIndustryNames.map((name) => `「${name}」`).join("")}分野の外国人が登録されています。この分野を外すと、受験手続および学習進捗にて管理ができなくなりますが、本当によろしいですか？`}
        </Typography>
      </ConfirmSaveModal>
      <DuplicateCompanyModal open={duplicateModal.open} onClose={handleDuplicateClose} onSave={handleSaveData} companies={companyExist} />
    </>
  );
};

const ButtonSave = styled(Button)`
  width: 88px;
`;
